const API_BASE = "/api";

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  token?: string | null,
): Promise<T> {
  const headers: Record<string, string> = {};

  if (options.body && !(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...headers,
      ...(options.headers as Record<string, string> | undefined),
    },
  });

  if (!response.ok) {
    let detail = response.statusText;

    try {
      const data = await response.json();
      if (typeof data === "object" && data !== null && "detail" in data) {
        const detailValue = (data as { detail?: unknown }).detail;
        if (typeof detailValue === "string") {
          detail = detailValue;
        }
      }
    } catch {
      // ignore parse errors
    }

    throw new ApiError(response.status, String(detail));
  }

  return response.json() as Promise<T>;
}

export type QuotaRequestMessageRecord = {
  id: string;
  created_at_timestamp: number;
  comment: string;
  quota_request_id: string;
};

export type SupportCaseMessageRecord = {
  aws_id: string;
  timestamp: number;
  message: string;
  aws_support_case_id: string;
  submitted_by: string;
  quota_request_id: string;
  created_at_timestamp: number;
};

export type ProjectRecord = {
  id: string;
  name: string;
  identifier: string;
  created_at_timestamp?: number;
};

export type QuotaRequestOperatorPatchBody = {
  project_id?: string | null;
  status?: string;
  priority?: string;
};

export type QuotaRequestRecord = {
  id: string;
  created_at_timestamp?: number;
  requested_at_timestamp?: number;
  aws_account_id?: string;
  aws_region?: string;
  deployment_environment_name?: string;
  aws_status?: string;
  aws_support_case_id?: string;
  status?: string;
  project_id?: string;
  priority?: string;
  quota_name?: string;
  service_code?: string;
};

export type QuotaRequestListingResponse = {
  sort_id: string;
  quota_request_ids: string[];
};

export type LoginResponse = {
  access_token: string;
  token_type: string;
  refresh_token?: string | null;
};

export type UserSessionStatus = {
  is_expired: boolean;
  session_expires_at_timestamp: number | null;
};

export const api = {
  login: (email: string, password: string) =>
    request<LoginResponse>("/auth/login/json", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  getMe: (token: string) => request<import("./types").User>("/auth/me", {}, token),

  getUserStatus: (token: string) =>
    request<UserSessionStatus>("/auth/user_status", {}, token),

  extendSession: (token: string, refreshToken: string | null) =>
    request<LoginResponse>(
      "/auth/extend_session",
      {
        method: "POST",
        body: JSON.stringify({
          refresh_token: refreshToken,
        }),
      },
      token,
    ),

  listQuotaRequestsByStatus: (token: string, status: string) =>
    request<Record<string, unknown>[]>(
      "/quota_request/list/{status}".replace(
        "{status}",
        encodeURIComponent(status),
      ),
      {},
      token,
    ),

  listQuarantineQuotaRequests: (token: string) =>
    request<Record<string, unknown>[]>(
      "/quota_request/list/quarantine",
      {},
      token,
    ),

  listAllVisibleQuotaRequests: (token: string, includeClosed = false) =>
    request<Record<string, unknown>[]>(
      "/quota_request/list?include_closed={include_closed}".replace(
        "{include_closed}",
        includeClosed ? "true" : "false",
      ),
      {},
      token,
    ),

  searchQuotaRequests: (token: string, searchQuery: string) =>
    request<Record<string, unknown>[]>(
      "/quota_request/search?q={search_query}".replace(
        "{search_query}",
        encodeURIComponent(searchQuery),
      ),
      {},
      token,
    ),

  listProjects: (token: string) =>
    request<ProjectRecord[]>("/project/list", {}, token),

  createProject: (token: string, name: string) =>
    request<ProjectRecord>("/project", {
      method: "POST",
      body: JSON.stringify({ name }),
    }, token),

  getQuotaRequest: (token: string, quotaRequestId: string) =>
    request<QuotaRequestRecord>(
      "/quota_request/{quota_request_id}".replace(
        "{quota_request_id}",
        encodeURIComponent(quotaRequestId),
      ),
      {},
      token,
    ),

  patchQuotaRequest: (token: string, quotaRequestId: string, patchBody: QuotaRequestOperatorPatchBody) =>
    request<Record<string, unknown>>(
      "/quota_request/{quota_request_id}".replace(
        "{quota_request_id}",
        encodeURIComponent(quotaRequestId),
      ),
      {
        method: "PATCH",
        body: JSON.stringify(patchBody),
      },
      token,
    ),

  createQuotaRequestListing: (token: string, quotaRequestIds: string[]) =>
    request<QuotaRequestListingResponse>(
      "/quota_request/listing",
      {
        method: "POST",
        body: JSON.stringify({ quota_request_ids: quotaRequestIds }),
      },
      token,
    ),

  getQuotaRequestListing: (token: string, sortId: string) =>
    request<QuotaRequestListingResponse>(
      "/quota_request/listing/{sort_id}".replace(
        "{sort_id}",
        encodeURIComponent(sortId),
      ),
      {},
      token,
    ),

  listQuotaRequestMessages: (token: string, quotaRequestId: string) =>
    request<QuotaRequestMessageRecord[]>(
      "/quota_request/{quota_request_id}/message/list".replace(
        "{quota_request_id}",
        encodeURIComponent(quotaRequestId),
      ),
      {},
      token,
    ),

  createQuotaRequestMessage: (token: string, quotaRequestId: string, comment: string) =>
    request<QuotaRequestMessageRecord>(
      "/quota_request/{quota_request_id}/message".replace(
        "{quota_request_id}",
        encodeURIComponent(quotaRequestId),
      ),
      {
        method: "POST",
        body: JSON.stringify({ comment }),
      },
      token,
    ),

  listSupportCaseMessages: (token: string, quotaRequestId: string) =>
    request<SupportCaseMessageRecord[]>(
      "/quota_request/{quota_request_id}/support_case/message/list".replace(
        "{quota_request_id}",
        encodeURIComponent(quotaRequestId),
      ),
      {},
      token,
    ),
};
